import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('gpi_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('gpi_token');
      localStorage.removeItem('gpi_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (username, password) => api.post('/auth/login', { username, password });
export const getMe = () => api.get('/auth/me');

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');
export const getDocumentTypesDashboard = (params) => api.get('/dashboard/document-types', { params });
export const exportDocumentTypesDashboard = (params) => {
  const queryString = new URLSearchParams(
    Object.entries(params || {}).filter(([_, v]) => v !== null && v !== undefined && v !== 'all')
  ).toString();
  const url = `${API_BASE_URL}/dashboard/document-types/export${queryString ? '?' + queryString : ''}`;
  window.location.href = url;
};

// Documents
export const uploadDocument = (formData) => api.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const listDocuments = (params) => api.get('/documents', { params });
export const getDocument = (id) => api.get(`/documents/${id}`);
export const updateDocument = (id, data) => api.put(`/documents/${id}`, data);
export const linkDocument = (id) => api.post(`/documents/${id}/link`);
export const deleteDocument = (id) => api.delete(`/documents/${id}`);
export const resubmitDocument = (id) => api.post(`/documents/${id}/reprocess?reclassify=true`);
export const verifyBCDocumentLink = (id) => api.post(`/bc-document-events/documents/${id}/verify-link`);

// Square9 Workflow Retry
export const retryDocument = (id, reason = 'Manual retry') => api.post(`/documents/${id}/retry?reason=${encodeURIComponent(reason)}`);
export const resetDocumentRetries = (id, reason = 'Manual reset') => api.post(`/documents/${id}/reset-retries?reason=${encodeURIComponent(reason)}`);
export const getSquare9Status = (id) => api.get(`/documents/${id}/square9-status`);
export const getSquare9StageCounts = () => api.get('/square9/stage-counts');

// Bulk operations
export const bulkRetryDocuments = async (docIds, reason = 'Bulk retry') => {
  const results = { success: [], failed: [] };
  for (const id of docIds) {
    try {
      const res = await retryDocument(id, reason);
      results.success.push({ id, data: res.data });
    } catch (err) {
      results.failed.push({ id, error: err.response?.data?.detail || err.message });
    }
  }
  return results;
};

export const bulkResubmitDocuments = async (docIds) => {
  const results = { success: [], failed: [] };
  for (const id of docIds) {
    try {
      const res = await resubmitDocument(id);
      results.success.push({ id, data: res.data });
    } catch (err) {
      results.failed.push({ id, error: err.response?.data?.detail || err.message });
    }
  }
  return results;
};

export const bulkDeleteDocuments = async (docIds) => {
  const results = { success: [], failed: [] };
  for (const id of docIds) {
    try {
      await deleteDocument(id);
      results.success.push({ id });
    } catch (err) {
      results.failed.push({ id, error: err.response?.data?.detail || err.message });
    }
  }
  return results;
};

// Workflows
export const listWorkflows = (params) => api.get('/workflows', { params });
export const getWorkflow = (id) => api.get(`/workflows/${id}`);
export const retryWorkflow = (id) => api.post(`/workflows/${id}/retry`);

// BC Proxy
export const getBcCompanies = () => api.get('/bc/companies');
export const getBcSalesOrders = (search) => api.get('/bc/sales-orders', { params: { search } });

// Settings
export const getSettingsStatus = () => api.get('/settings/status');
export const getSettingsConfig = () => api.get('/settings/config');
export const updateSettingsConfig = (data) => api.put('/settings/config', data);
export const testConnection = (service) => api.post(`/settings/test-connection?service=${service}`);

// Job Types (Email Parser Config)
export const getJobTypes = () => api.get('/settings/job-types');
export const getJobType = (jobType) => api.get(`/settings/job-types/${jobType}`);
export const updateJobType = (jobType, data) => api.put(`/settings/job-types/${jobType}`, data);

// Email Watcher Config (Legacy)
export const getEmailWatcherConfig = () => api.get('/settings/email-watcher');
export const updateEmailWatcherConfig = (data) => api.put('/settings/email-watcher', data);
export const subscribeEmailWatcher = (webhookUrl) => api.post(`/settings/email-watcher/subscribe?webhook_url=${encodeURIComponent(webhookUrl)}`);

// Mailbox Sources CRUD
export const listMailboxSources = () => api.get('/settings/mailbox-sources');
export const getMailboxSource = (id) => api.get(`/settings/mailbox-sources/${id}`);
export const createMailboxSource = (data) => api.post('/settings/mailbox-sources', data);
export const updateMailboxSource = (id, data) => api.put(`/settings/mailbox-sources/${id}`, data);
export const deleteMailboxSource = (id) => api.delete(`/settings/mailbox-sources/${id}`);
export const testMailboxConnection = (id) => api.post(`/settings/mailbox-sources/${id}/test-connection`);
export const pollMailboxNow = (id) => api.post(`/settings/mailbox-sources/${id}/poll-now`);
export const getMailboxPollingStatus = () => api.get('/settings/mailbox-sources/polling-status');

// Email Stats Dashboard
export const getEmailStats = () => api.get('/dashboard/email-stats');

// Document Intake & Classification
export const intakeDocument = (formData) => api.post('/documents/intake', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const classifyDocument = (id) => api.post(`/documents/${id}/classify`);

// AP Invoice Workflow Queues
export const getWorkflowStatusCounts = () => api.get('/workflows/ap_invoice/status-counts');
export const getVendorPendingQueue = (params) => api.get('/workflows/ap_invoice/vendor-pending', { params });
export const getBcValidationPendingQueue = (params) => api.get('/workflows/ap_invoice/bc-validation-pending', { params });
export const getBcValidationFailedQueue = (params) => api.get('/workflows/ap_invoice/bc-validation-failed', { params });
export const getDataCorrectionPendingQueue = (params) => api.get('/workflows/ap_invoice/data-correction-pending', { params });
export const getReadyForApprovalQueue = (params) => api.get('/workflows/ap_invoice/ready-for-approval', { params });
export const getWorkflowMetrics = (days) => api.get('/workflows/ap_invoice/metrics', { params: { days } });

// Generic Workflow APIs (multi-document type support)
export const getGenericQueue = (docType, params) => api.get('/workflows/generic/queue', { params: { doc_type: docType, ...params } });
export const getStatusCountsByType = () => api.get('/workflows/generic/status-counts-by-type');
export const getMetricsByType = (days, docType) => api.get('/workflows/generic/metrics-by-type', { params: { days, doc_type: docType } });

// AP Invoice Workflow Actions
export const setVendor = (docId, vendorNo, vendorName, actor) => api.post(`/workflows/ap_invoice/${docId}/set-vendor`, { vendor_no: vendorNo, vendor_name: vendorName, actor });
export const updateFields = (docId, fields, actor) => api.post(`/workflows/ap_invoice/${docId}/update-fields`, { ...fields, actor });
export const overrideBcValidation = (docId, reason, actor) => api.post(`/workflows/ap_invoice/${docId}/override-bc-validation`, { reason, actor });
export const startApproval = (docId, actor) => api.post(`/workflows/ap_invoice/${docId}/start-approval`, { actor });
export const approveDocument = (docId, comment, actor) => api.post(`/workflows/ap_invoice/${docId}/approve`, { comment, actor });
export const rejectDocument = (docId, reason, actor) => api.post(`/workflows/ap_invoice/${docId}/reject`, { reason, actor });

// Generic Workflow Actions (for any doc type)
export const exportDocument = (docId, destination, user) => api.post(`/workflows/${docId}/export`, null, { params: { export_destination: destination, user } });

// Get AP Dashboard metrics (from doc-types dashboard filtered to AP)
export const getAPDashboardMetrics = () => api.get('/dashboard/document-types', { params: { doc_type: 'AP_INVOICE' } });

// =============================================================================
// PILOT APIs
// =============================================================================

// Get pilot status and configuration
export const getPilotStatus = () => api.get('/pilot/status');

// Get pilot daily metrics
export const getPilotDailyMetrics = (phase = 'shadow_pilot_v1', date = null) => 
  api.get('/pilot/daily-metrics', { params: { phase, date } });

// Get pilot logs
export const getPilotLogs = (params = {}) => 
  api.get('/pilot/logs', { params: { phase: 'shadow_pilot_v1', ...params } });

// Get pilot accuracy report
export const getPilotAccuracy = (phase = 'shadow_pilot_v1') => 
  api.get('/pilot/accuracy', { params: { phase } });

// Get pilot trend data
export const getPilotTrend = (phase = 'shadow_pilot_v1', days = 14) => 
  api.get('/pilot/trend', { params: { phase, days } });

// Send daily pilot summary email manually
export const sendPilotSummaryEmail = () => 
  api.post('/pilot/send-daily-summary');

// Get pilot email logs
export const getPilotEmailLogs = (limit = 20, skip = 0) =>
  api.get('/pilot/email-logs', { params: { limit, skip } });

// Get pilot email config
export const getPilotEmailConfig = () =>
  api.get('/pilot/email-config');

// =============================================================================
// AP REVIEW APIs
// =============================================================================

// Search vendors from BC
export const searchVendors = (query = '', limit = 50) => 
  api.get('/ap-review/vendors', { params: { q: query, limit } });

// Get vendor by ID
export const getVendor = (vendorId) => 
  api.get(`/ap-review/vendors/${vendorId}`);

// Search purchase orders from BC
export const searchPurchaseOrders = (vendorId = null, limit = 50) => 
  api.get('/ap-review/purchase-orders', { params: { vendor_id: vendorId, limit } });

// Save AP review edits
export const saveAPReview = (docId, data) => 
  api.put(`/ap-review/documents/${docId}`, data);

// Mark document ready for posting
export const markReadyForPost = (docId) => 
  api.post(`/ap-review/documents/${docId}/mark-ready`);

// Post document to BC
export const postToBC = (docId, data = null) => 
  api.post(`/ap-review/documents/${docId}/post-to-bc`, data);

// Get BC posting status
export const getBCPostingStatus = (docId) => 
  api.get(`/ap-review/documents/${docId}/bc-status`);

// Extract invoice data using AI
export const extractInvoiceData = (docId) => 
  api.post(`/ap-review/documents/${docId}/extract-invoice-data`);

// Get extraction status
export const getExtractionStatus = (docId) => 
  api.get(`/ap-review/documents/${docId}/extraction-status`);

export default api;